import { FC, useEffect } from 'react';
import TableModule from './TableModule';
import { Form } from 'antd';
import { isNotEmpty } from '@lhb/func';
import { SaleTargetConfig, SaleTargetNames } from '../ts-config';
import { fieldNameSaleTarget } from './fieldname';
import { fixIntParam, fixPrecisionParam } from './transfer2value';

/**
 * @Description 销售指标
 */
const SaleTarget: FC<{
  form: any;
  formItemData: any;
  disabled?: boolean;
  onChange: any;
}> = ({
  form,
  formItemData,
  disabled,
  onChange
}) => {

  // 终端销售指标（第一年）
  const saleTargetFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.saleTarget),
    form
  );
  // 终端销售指标（第二年）
  const saleTargetSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.saleTarget),
    form
  );

  // 老品销售吊牌销售金额（第一年）
  const oldProductAmountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.oldProductAmount),
    form
  );
  // 老品销售吊牌销售金额（第二年）
  const oldProductAmountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.oldProductAmount),
    form
  );

  // 新品销售吊牌销售金额（第一年）
  const newProductAmountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.newProductAmount),
    form
  );
  // 新品销售吊牌销售金额（第二年）
  const newProductAmountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.newProductAmount),
    form
  );

  // 老品销售额占比（第一年）
  const oldProductProportionFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.oldProductProportion),
    form
  );
  // 老品销售额占比（第二年）
  const oldProductProportionSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.oldProductProportion),
    form
  );

  // 新品销售额占比（第一年）
  const newProductProportionFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.newProductProportion),
    form
  );
  // 新品销售额占比（第二年）
  const newProductProportionSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.newProductProportion),
    form
  );

  // 老品销售折扣（第一年）
  const oldProductDiscountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.oldProductDiscount),
    form
  );
  // 老品销售折扣（第二年）
  const oldProductDiscountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.oldProductDiscount),
    form
  );

  // 新品销售折扣（第一年）
  const newProductDiscountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.newProductDiscount),
    form
  );
  // 新品销售折扣（第二年）
  const newProductDiscountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.newProductDiscount),
    form
  );

  /**
   * @description 吊牌销售总金额
   */
  useEffect(() => {
    const name = fieldNameSaleTarget(true, SaleTargetNames.saleAmount);
    const val = calcSaleAmount(oldProductAmountFirst, newProductAmountFirst);
    form.setFieldValue(name, val);
  }, [oldProductAmountFirst, newProductAmountFirst]);

  useEffect(() => {
    const name = fieldNameSaleTarget(false, SaleTargetNames.saleAmount);
    const val = calcSaleAmount(oldProductAmountSecond, newProductAmountSecond);
    form.setFieldValue(name, val);
  }, [oldProductAmountSecond, newProductAmountSecond]);

  /**
   * @description 吊牌销售总金额
   * @param oldProductAmount
   * @param newProductAmount
   * @return 老品销售吊牌销售金额+新品销售吊牌销售金额
   */
  const calcSaleAmount = (oldProductAmount, newProductAmount) => {
    let val: any = null;
    isNotEmpty(oldProductAmount) && (val += (+oldProductAmount));
    isNotEmpty(newProductAmount) && (val += (+newProductAmount));
    isNotEmpty(val) && (val = fixIntParam(val));
    return val;
  };

  /**
   * @description 老品销售吊牌销售金额：终端销售指标*老品销售占比/老品销售折扣
   */
  useEffect(() => {
    const name = fieldNameSaleTarget(true, SaleTargetNames.oldProductAmount);
    const val = calcProductAmount(saleTargetFirst, oldProductProportionFirst, oldProductDiscountFirst);
    form.setFieldValue(name, val);
  }, [saleTargetFirst, oldProductProportionFirst, oldProductDiscountFirst]);

  useEffect(() => {
    const name = fieldNameSaleTarget(false, SaleTargetNames.oldProductAmount);
    const val = calcProductAmount(saleTargetSecond, oldProductProportionSecond, oldProductDiscountSecond);
    form.setFieldValue(name, val);
  }, [saleTargetSecond, oldProductProportionSecond, oldProductDiscountSecond]);

  /**
   * @description 新品销售吊牌销售金额：终端销售指标*新品销售占比/新品销售折扣
   */
  useEffect(() => {
    const name = fieldNameSaleTarget(true, SaleTargetNames.newProductAmount);
    const val = calcProductAmount(saleTargetFirst, newProductProportionFirst, newProductDiscountFirst);
    form.setFieldValue(name, val);
  }, [saleTargetFirst, newProductProportionFirst, newProductDiscountFirst]);

  useEffect(() => {
    const name = fieldNameSaleTarget(false, SaleTargetNames.newProductAmount);
    const val = calcProductAmount(saleTargetSecond, newProductProportionSecond, newProductDiscountSecond);
    form.setFieldValue(name, val);
  }, [saleTargetSecond, newProductProportionSecond, newProductDiscountSecond]);

  /**
   * @description 老品/新品销售吊牌销售金额
   * @param saleTarget
   * @param proportion
   * @param discount
   * @return
   *    老品销售吊牌销售金额
   *         终端销售指标*老品销售占比/老品销售折扣
   *    新品销售吊牌销售金额
   *         终端销售指标*新品销售占比/新品销售折扣
   */
  const calcProductAmount = (saleTarget, proportion, discount) => {
    return isNotEmpty(saleTarget) && isNotEmpty(proportion) && discount ? fixIntParam((+saleTarget) * (+proportion / 100.0) / (+discount / 100.0)) : null;
  };

  /**
   * @description 老品销售额占比：100%-新品销售额占比
   */
  useEffect(() => {
    const name = fieldNameSaleTarget(true, SaleTargetNames.oldProductProportion);
    const val = calcOldProductProportion(newProductProportionFirst);
    form.setFieldValue(name, val);
  }, [newProductProportionFirst]);

  useEffect(() => {
    const name = fieldNameSaleTarget(false, SaleTargetNames.oldProductProportion);
    const val = calcOldProductProportion(newProductProportionSecond);
    form.setFieldValue(name, val);
  }, [newProductProportionSecond]);

  /**
   * @description 老品销售额占比：100%-新品销售额占比
   * @param newProductProportion
   * @return
   */
  const calcOldProductProportion = (newProductProportion) => {
    return isNotEmpty(newProductProportion) ? fixPrecisionParam(100 - (+newProductProportion)) : null;
  };

  return (
    <TableModule
      moduleConfig={SaleTargetConfig}
      form={form}
      formItemData={formItemData}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

export default SaleTarget;
